import type { GameEvent, ViewMessage } from '@ubs/protocol';
import type { Transport, TransportHandlers } from './transport.js';

/**
 * Online masa bağlantısı — ham WebSocket.
 *
 * Her mesaj tek bir JSON zarfına giriyor: `{ t: tip, d: yük }`. Tip adları
 * sunucudaki masa ile birebir aynı; taşıma katmanı dışında hiçbir şey
 * bilmiyor. Yerel oyunla AYNI `TransportHandlers` arayüzünü doldurduğu için
 * masa bileşeni hangi modda olduğunu ayırt edemiyor.
 */

const SERVER = import.meta.env.VITE_SERVER_URL as string | undefined;

export const onlineAvailable = (): boolean => Boolean(SERVER);

interface Envelope {
  t: string;
  d: unknown;
}

/** Bağlantı kurulup masaya kabul edilene kadar bekler. */
export function connectOnline(
  options: { name: string; code: string; pass?: string },
  handlers: TransportHandlers,
): Promise<Transport> {
  if (!SERVER) return Promise.reject(new Error('Online masa henüz açık değil'));

  const url = new URL('/table', SERVER);
  url.searchParams.set('code', options.code);
  url.searchParams.set('name', options.name);
  // Ortak parola: giriş ekranındaki sorunun cevabı. Sunucuda `ACCESS_CODE`
  // tanımlıysa eşleşmeyen bağlantı 4401 ile kapatılıyor.
  if (options.pass) url.searchParams.set('pass', options.pass);

  const ws = new WebSocket(url.toString());
  let disposed = false;

  return new Promise<Transport>((resolve, reject) => {
    /**
     * "Açıldı" tek başına "kabul edildi" DEĞİL.
     *
     * Sunucu reddi de el sıkışmadan sonra, kapanış koduyla bildiriyor
     * (tarayıcı başarısız el sıkışmasının durum kodunu vermiyor). Bu yüzden
     * kabulün işareti `welcome` mesajı; ondan önce gelen kapanış bir red.
     */
    let welcomed = false;

    const transport: Transport = {
      send(type, payload) {
        if (disposed || ws.readyState !== WebSocket.OPEN) return;
        ws.send(JSON.stringify({ t: type, d: payload }));
      },
      dispose() {
        disposed = true;
        // 1000 = "masadan kalktım". Sunucu bunu bilerek çıkış sayıyor.
        if (ws.readyState === WebSocket.OPEN) ws.close(1000, 'çıkış');
        else ws.close();
      },
    };

    ws.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return;
      let envelope: Envelope;
      try {
        envelope = JSON.parse(event.data) as Envelope;
      } catch {
        return; // bozuk çerçeve sessizce düşer
      }

      switch (envelope.t) {
        case 'welcome':
          welcomed = true;
          // Dinleyiciler bağlandı; masanın durumunu ve hazır mesajları iste.
          transport.send('hello', {});
          resolve(transport);
          break;
        case 'view':
          handlers.view(envelope.d as ViewMessage);
          break;
        case 'event':
          handlers.event(envelope.d as GameEvent);
          break;
        case 'lobby': {
          const { players, needed } = envelope.d as {
            players: { name: string }[];
            needed: number;
          };
          handlers.lobby(
            players.map((p) => p.name),
            needed,
          );
          break;
        }
        case 'starting':
          handlers.starting((envelope.d as { startsAt: number }).startsAt);
          break;
        case 'phrases':
          handlers.phrases((envelope.d as { phrases: string[] }).phrases);
          break;
        case 'error':
          handlers.error(String(envelope.d));
          break;
      }
    });

    ws.addEventListener('close', (event) => {
      if (!welcomed) {
        reject(new Error(event.reason || 'Masaya bağlanılamadı'));
        return;
      }
      if (!disposed) handlers.error('Bağlantı koptu');
    });

    ws.addEventListener('error', () => {
      if (!welcomed) reject(new Error('Sunucuya ulaşılamadı'));
    });
  });
}

/**
 * Salon listesi — masaların canlı doluluğu.
 *
 * Ayrı bir soket: salon ekranı masaya oturmadan da güncel kalmalı. Sunucu
 * yoksa hiç açılmıyor ve salon boş masalar gösteriyor.
 */
export function watchLobby(onTables: (tables: Record<string, unknown>) => void): () => void {
  if (!SERVER) return () => {};

  const url = new URL('/lobby', SERVER);
  const ws = new WebSocket(url.toString());

  ws.addEventListener('message', (event) => {
    if (typeof event.data !== 'string') return;
    try {
      const envelope = JSON.parse(event.data) as Envelope;
      if (envelope.t === 'tables') onTables(envelope.d as Record<string, unknown>);
    } catch {
      /* bozuk çerçeve sessizce düşer */
    }
  });

  return () => ws.close();
}
